/**
 * Scenario Comparator Controller (EPIC-026.6)
 *
 * Builds the side-by-side comparison of 2 to 4 saved scenarios on
 * `/simulateur-comparateur.html`, reading data from `outputs` JSONB stored
 * by EPIC-026.4 — no `/calculate` call (D-26.6.E).
 *
 * Selection is exposed via the `?ids=` querystring so the page is back-
 * friendly and shareable (D-26.6.G). Initial render:
 *  - GET /simulator/scenarios → populate checkboxes.
 *  - Pre-check the ids from the querystring (capped at 4 — D-26.6.F).
 *  - If ≥ 2 selected, fetch each scenario by id and render columns + verdict
 *    + warnings.
 *  - Otherwise, show the empty-state encart.
 *
 * Rendering goes exclusively through `<template>` clones — no HTML in JS.
 */

import BaseController from "./base_controller.js";
import { api } from "../services/api.js";

const MAX_SELECTION = 4;

export default class ScenarioComparatorController extends BaseController {
  static targets = [
    "filters",
    "counter",
    "emptyState",
    "grid",
    "verdict",
    "warnings",
  ];

  connect() {
    this.scenarios = [];
    this.selectedIds = this._readSelectionFromUrl();
    this.refresh();
  }

  async refresh() {
    try {
      const res = await api.get("/simulator/scenarios", { perPage: 100 });
      const items = Array.isArray(res?.data) ? res.data : [];
      this.scenarios = items.filter((s) => s.status === "active");
    } catch (_e) {
      this.scenarios = [];
    }
    this.selectedIds = this.selectedIds.filter((id) =>
      this.scenarios.some((s) => s.id === id),
    );
    this.renderFilters();
    this.renderComparison();
  }

  // ---------------------------------------------------------------------------
  // Selection / URL sync
  // ---------------------------------------------------------------------------

  _readSelectionFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("ids") || "";
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s !== "")
      .slice(0, MAX_SELECTION);
  }

  _writeSelectionToUrl() {
    const params = new URLSearchParams(window.location.search);
    if (this.selectedIds.length === 0) {
      params.delete("ids");
    } else {
      params.set("ids", this.selectedIds.join(","));
    }
    const query = params.toString();
    const url = `${window.location.pathname}${query ? `?${query}` : ""}`;
    window.history.replaceState(null, "", url);
  }

  onSelectionChange(event) {
    const checkbox = event.currentTarget;
    const id = checkbox.value;
    if (!id) return;

    if (checkbox.checked) {
      if (this.selectedIds.includes(id)) return;
      if (this.selectedIds.length >= MAX_SELECTION) {
        checkbox.checked = false;
        this.showError(`Vous ne pouvez comparer que ${MAX_SELECTION} scénarios à la fois.`);
        return;
      }
      this.selectedIds.push(id);
    } else {
      this.selectedIds = this.selectedIds.filter((sid) => sid !== id);
    }
    this._writeSelectionToUrl();
    this._updateCounter();
    this._updateDisabledCheckboxes();
    this.renderComparison();
  }

  // ---------------------------------------------------------------------------
  // Filters (checkboxes)
  // ---------------------------------------------------------------------------

  renderFilters() {
    if (!this.hasFiltersTarget) return;
    const root = this.filtersTarget;
    while (root.firstChild) root.removeChild(root.firstChild);

    this.scenarios.forEach((s) => {
      const fragment = this.cloneTemplate("tpl-comparator-checkbox");
      const input = fragment.querySelector("input[type=checkbox]");
      const span = fragment.querySelector("span");
      if (input) {
        input.value = s.id;
        input.checked = this.selectedIds.includes(s.id);
      }
      if (span) span.textContent = s.title;
      root.appendChild(fragment);
    });

    this._updateCounter();
    this._updateDisabledCheckboxes();
  }

  _updateCounter() {
    if (!this.hasCounterTarget) return;
    const n = this.selectedIds.length;
    this.counterTarget.textContent = n === 0
      ? `0 sélectionné · max ${MAX_SELECTION}`
      : `${n} sélectionné${n > 1 ? "s" : ""} · max ${MAX_SELECTION}`;
  }

  _updateDisabledCheckboxes() {
    if (!this.hasFiltersTarget) return;
    const capped = this.selectedIds.length >= MAX_SELECTION;
    this.filtersTarget.querySelectorAll("input[type=checkbox]").forEach((cb) => {
      cb.disabled = capped && !cb.checked;
    });
  }

  // ---------------------------------------------------------------------------
  // Comparison rendering
  // ---------------------------------------------------------------------------

  async renderComparison() {
    const showEmpty = this.selectedIds.length < 2;
    if (this.hasEmptyStateTarget) this.emptyStateTarget.hidden = !showEmpty;
    if (this.hasGridTarget) {
      while (this.gridTarget.firstChild) this.gridTarget.removeChild(this.gridTarget.firstChild);
    }
    if (this.hasVerdictTarget) {
      while (this.verdictTarget.firstChild) this.verdictTarget.removeChild(this.verdictTarget.firstChild);
    }
    if (this.hasWarningsTarget) {
      while (this.warningsTarget.firstChild) this.warningsTarget.removeChild(this.warningsTarget.firstChild);
    }
    if (showEmpty) return;

    const fetched = await Promise.all(
      this.selectedIds.map((id) => api.get(`/simulator/scenarios/${encodeURIComponent(id)}`)),
    );
    const valid = fetched.filter((s) => s && s.inputs && s.outputs);
    if (valid.length < 2) return;

    const reference = this._pickReference(valid);
    this._renderGrid(valid, reference);
    this._renderVerdict(valid, reference);
    this._renderWarnings(valid);
  }

  _pickReference(scenarios) {
    let best = scenarios[0];
    let bestNet = this._netIncome(scenarios[0]);
    scenarios.forEach((s) => {
      const net = this._netIncome(s);
      if (net > bestNet) {
        best = s;
        bestNet = net;
      }
    });
    return best;
  }

  _renderGrid(scenarios, reference) {
    if (!this.hasGridTarget) return;
    const cols = scenarios.length;

    const headerRow = this._gridRow(cols, "header");
    scenarios.forEach((s) => {
      const fragment = this.cloneTemplate("tpl-comparator-column");
      const col = fragment.querySelector("[data-slot-attr]");
      if (col) col.dataset.columnRole = s.id === reference.id ? "reference" : "alternative";
      const refPill = fragment.querySelector('[data-slot-show="isReference"]');
      const altPill = fragment.querySelector('[data-slot-show="isAlternative"]');
      if (refPill) refPill.hidden = s.id !== reference.id;
      if (altPill) altPill.hidden = s.id === reference.id;
      this.fillSlots(fragment, {
        title: s.title,
        subtitle: this._subtitleFor(s),
      });
      if (s.id === reference.id) {
        const wrapper = fragment.querySelector("div");
        if (wrapper) wrapper.style.background = "var(--accent-soft)";
      }
      headerRow.appendChild(fragment);
    });
    this.gridTarget.appendChild(headerRow);

    this.gridTarget.appendChild(this._metricRow(scenarios, "CA HT projeté", (s) =>
      this._formatMoney(this._totals(s).revenueEur),
      (s) => this._caDetail(s),
    ));
    this.gridTarget.appendChild(this._metricRow(scenarios, "Cotisations URSSAF", (s) =>
      this._formatMoney(this._totals(s).urssafContributionsEur),
      (s) => {
        const saving = this._totals(s).acreSavingEur;
        return saving > 0 ? `− ${this._formatMoney(saving)} grâce à l'ACRE` : "Sans ACRE";
      },
    ));
    this.gridTarget.appendChild(this._metricRow(scenarios, "Impôt sur le revenu", (s) =>
      this._formatMoney(this._totals(s).incomeTaxEur),
      (s) => (s.inputs.vlOption ? "Versement libératoire" : "Barème progressif"),
    ));
    this.gridTarget.appendChild(this._metricRow(scenarios, "ARE / ARCE perçu", (s) =>
      this._allocationTotal(s),
      (s) => this._allocationDetail(s),
    ));
    this.gridTarget.appendChild(this._metricRow(scenarios, "Revenu net cumulé", (s) =>
      this._formatMoney(this._totals(s).netIncomeEur),
      (s) => this._netDelta(s, reference),
      (s) => s.id === reference.id,
    ));

    this.gridTarget.appendChild(this._milestonesRow(scenarios));
  }

  _gridRow(cols, role) {
    const div = document.createElement("div");
    div.style.display = "grid";
    div.style.gridTemplateColumns = `repeat(${cols}, 1fr)`;
    div.style.borderBottom = "1px solid var(--border)";
    if (role === "header") div.dataset.row = "header";
    return div;
  }

  _metricRow(scenarios, label, valueFn, detailFn, isHighlightFn = null) {
    const row = this._gridRow(scenarios.length, "metric");
    scenarios.forEach((s) => {
      const fragment = this.cloneTemplate("tpl-comparator-metric-row");
      this.fillSlots(fragment, {
        label: `— ${label}`,
        value: valueFn(s),
        detail: detailFn ? detailFn(s) : "",
      });
      if (isHighlightFn && isHighlightFn(s)) {
        const cell = fragment.querySelector("div");
        if (cell) {
          cell.style.background = "var(--accent-soft)";
          const valueEl = cell.querySelector('[data-slot="value"]');
          if (valueEl) valueEl.classList.add("text-accent");
        }
      }
      row.appendChild(fragment);
    });
    return row;
  }

  _milestonesRow(scenarios) {
    const row = this._gridRow(scenarios.length, "milestones");
    row.style.borderBottom = "0";
    scenarios.forEach((s) => {
      const cell = document.createElement("div");
      cell.style.padding = "20px 28px";
      cell.style.borderRight = "1px solid var(--border)";

      const heading = document.createElement("div");
      heading.className = "nm-card__h";
      heading.style.marginBottom = "12px";
      heading.textContent = "— Jalons clés";
      cell.appendChild(heading);

      const ul = document.createElement("ul");
      ul.className = "nm-timeline";
      const timeline = Array.isArray(s.outputs?.timeline) ? s.outputs.timeline.slice(0, 4) : [];
      if (timeline.length === 0) {
        const li = document.createElement("li");
        const when = document.createElement("span");
        when.className = "nm-timeline__when";
        when.textContent = "—";
        const what = document.createElement("div");
        const span = document.createElement("span");
        span.className = "nm-timeline__what";
        span.textContent = "Aucun jalon sur la période simulée";
        what.appendChild(span);
        li.appendChild(when);
        li.appendChild(what);
        ul.appendChild(li);
      } else {
        timeline.forEach((m) => {
          const fragment = this.cloneTemplate("tpl-comparator-milestone");
          this.fillSlots(fragment, {
            when: this._formatShortDate(m.date),
            what: m.label || m.kind || "",
          });
          ul.appendChild(fragment);
        });
      }
      cell.appendChild(ul);
      row.appendChild(cell);
    });
    return row;
  }

  _renderVerdict(scenarios, reference) {
    if (!this.hasVerdictTarget) return;
    const refNet = this._netIncome(reference);
    const others = scenarios.filter((s) => s.id !== reference.id);
    const minDelta = others.reduce((acc, s) => {
      const d = refNet - this._netIncome(s);
      return acc === null || d < acc ? d : acc;
    }, null);

    const heading = document.createElement("div");
    heading.className = "nm-card__h";
    heading.textContent = "— Verdict comparatif";

    const title = document.createElement("h2");
    title.className = "nm-card__title";
    title.style.marginTop = "8px";
    const horizonMonths = reference.inputs?.simulationMonths || 0;
    title.textContent = `« ${reference.title} » reste le scénario le plus rémunérateur${horizonMonths ? ` sur ${horizonMonths} mois` : ""}.`;

    const para = document.createElement("p");
    para.className = "fs-14 text-muted";
    para.style.lineHeight = "1.6";
    para.style.margin = "0";
    para.textContent = minDelta !== null && minDelta > 0
      ? `Revenu net cumulé : ${this._formatMoney(refNet)}, soit +${this._formatMoney(minDelta)} face au scénario le plus proche.`
      : `Revenu net cumulé : ${this._formatMoney(refNet)}.`;

    this.verdictTarget.appendChild(heading);
    this.verdictTarget.appendChild(title);
    this.verdictTarget.appendChild(para);
  }

  _renderWarnings(scenarios) {
    if (!this.hasWarningsTarget) return;
    const horizons = new Set(scenarios.map((s) => s.inputs?.simulationMonths).filter((m) => m));
    if (horizons.size > 1) {
      const fragment = this.cloneTemplate("tpl-comparator-warning");
      const div = fragment.querySelector("div");
      if (div) div.classList.add("nm-banner--info");
      this.fillSlots(fragment, {
        text: "Comparaison à durée variable — les scénarios n'ont pas tous le même horizon. Les revenus nets affichés reflètent chaque durée.",
      });
      this.warningsTarget.appendChild(fragment);
    }
    const hasAre = scenarios.some((s) => s.inputs?.allocation === "are");
    if (hasAre) {
      const fragment = this.cloneTemplate("tpl-comparator-warning");
      const div = fragment.querySelector("div");
      if (div) div.classList.add("nm-banner--warning");
      this.fillSlots(fragment, {
        text: "Estimations ARE indicatives — France Travail applique des règles de plafonnement (SJR) non accessibles par API ; les montants ARE projetés peuvent différer du versement réel.",
      });
      this.warningsTarget.appendChild(fragment);
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _totals(scenario) {
    return scenario.outputs?.totals || {};
  }

  _netIncome(scenario) {
    return Number(this._totals(scenario).netIncomeEur || 0);
  }

  _allocationTotal(scenario) {
    const allocation = scenario.inputs?.allocation || "none";
    if (allocation === "none") return "—";
    const rows = scenario.outputs?.allocationComparison || [];
    if (rows.length === 0) return "—";
    const last = rows[rows.length - 1];
    const amount = allocation === "arce" ? last.arceCumulativeEur : last.areCumulativeEur;
    return amount ? `+ ${this._formatMoney(amount)}` : "—";
  }

  _allocationDetail(scenario) {
    const allocation = scenario.inputs?.allocation || "none";
    if (allocation === "are") return "ARE maintien · déclaration mensuelle";
    if (allocation === "arce") return "ARCE · capital 60 % en 2 tranches";
    return "Sans allocation chômage";
  }

  _netDelta(scenario, reference) {
    if (scenario.id === reference.id) return "Meilleur revenu net";
    const delta = this._netIncome(reference) - this._netIncome(scenario);
    return delta > 0
      ? `− ${this._formatMoney(delta)} vs ${reference.title}`
      : `+ ${this._formatMoney(-delta)} vs ${reference.title}`;
  }

  _subtitleFor(scenario) {
    const inputs = scenario.inputs || {};
    const regimes = [...new Set((inputs.activities || []).map((a) => this._regimeLabel(a.regime)))];
    const flags = [
      inputs.acreEligible ? "ACRE" : null,
      inputs.allocation === "are" ? "ARE" : null,
      inputs.allocation === "arce" ? "ARCE" : null,
      inputs.vlOption ? "VL" : null,
    ].filter(Boolean);
    const horizon = inputs.simulationMonths ? `${inputs.simulationMonths} mois` : null;
    const start = inputs.simulationDate ? `début ${this._formatShortDate(inputs.simulationDate)}` : null;
    return [regimes.join(" + "), ...flags, start, horizon].filter(Boolean).join(" · ");
  }

  _caDetail(scenario) {
    const activities = scenario.inputs?.activities || [];
    if (activities.length === 0) return "";
    const monthly = activities.reduce((sum, a) => sum + (Number(a.monthlyCa) || 0), 0);
    return `${activities.length} activité${activities.length > 1 ? "s" : ""} · ${this._formatMoney(monthly)}/mois`;
  }

  _regimeLabel(regime) {
    switch (regime) {
      case "bic_vente": return "BIC vente";
      case "bic_services": return "BIC services";
      case "bnc": return "BNC";
      default: return regime || "—";
    }
  }

  _formatMoney(value) {
    const n = Number(value || 0);
    return `${n.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
  }

  _formatShortDate(iso) {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" });
    } catch (_e) {
      return "—";
    }
  }
}
